/**
 * The editor's "Print guide key (no AI)" step, as a pure function.
 *
 * It is `keyImage` (transparent/keying.ts — the twin of scripts/transparent_overlay.py) plus the
 * one thing the editor needs on top: the alpha plane, split out of the keyed RGBA for the AI-edge
 * print mask and the persisted matte PNG. The RGBA itself goes to the canvas VERBATIM, which is
 * what makes the editor byte-identical to the Transparent tab's bulk export.
 *
 * Extracted from MaskEditor so the parity test can run the editor's branch and the bulk converter
 * on the same pixels; the component calls this and nothing else.
 */
import { keyImage, SHIPPED_KEYING } from "../transparent/keying.js";

/** How a shaded print guide is keyed (owner, 2026-10-13: "decide how much should be included"
 *  and smooth the edges of what is kept). The defaults are the key as it shipped. */
export interface GuideKeyOptions {
  /** "spread" grows from strong magenta through magenta-like pixels (the shipped key); "purity"
   *  makes each pixel as transparent as it is magenta, on a smooth ramp; "bulk" is the
   *  Transparent tab's key, which keeps the case's effects see-through (owner, 2026-10-14). */
  method?: "spread" | "purity" | "bulk";
  /** Bulk: the Transparent tab's four controls. */
  noiseFloor?: number;
  snapLow?: number;
  snapHigh?: number;
  flatSample?: number;
  /** Spread: 0..1, how weak a magenta still counts as print guide. 0.5 is the shipped key. */
  tolerance?: number;
  /** Purity: keying starts at this purity, 0..1. */
  start?: number;
  /** Purity: fully transparent at this purity, 0..1. */
  full?: number;
  /** Purity: 0..1, how much darker magenta stays opaque, so deep shadows keep some body. */
  darkKeep?: number;
  /** 0..4 px: the soft step where the key meets what it keeps. 1 is the shipped key. */
  softness?: number;
  /** 0..1: how much of the magenta cast is taken out of what the key keeps near the guide (the
   *  camera lip, the rim), leaving its light as grey. 0 is the shipped key. */
  colour?: number;
}

/** What the key keeps within this many pixels of the guide loses its magenta cast. */
const CAST_REACH = 8;

/**
 * Owner, 2026-10-14: "I only want to remove the magenta colour from there … make it black and
 * white". What is kept within `CAST_REACH` px of the `guide` (non-zero = guide) loses its
 * magenta cast and keeps its light, in place. `colour` 0 changes nothing.
 */
export function removeGuideCast(
  rgba: Uint8ClampedArray, guide: Uint8Array, width: number, height: number, colour: number,
): void {
  const take = Math.min(1, Math.max(0, colour));
  if (!take) return;
  const near = grow(guide, width, height, CAST_REACH);
  for (let i = 0; i < near.length; i++) {
    const o = i * 4;
    if (!near[i] || !rgba[o + 3]) continue;
    const r = rgba[o], g = rgba[o + 1], b = rgba[o + 2];
    const cast = Math.min(r, b) - g;
    if (cast <= 0) continue;
    const w = take * Math.min(1, cast / 8);
    const luma = 0.299 * r + 0.587 * g + 0.114 * b;
    rgba[o] = Math.round(r + (luma - r) * w);
    rgba[o + 1] = Math.round(g + (luma - g) * w);
    rgba[o + 2] = Math.round(b + (luma - b) * w);
  }
}

/** Purity of a pixel: how magenta it is, whatever its brightness. #FF00FF and #400040 are 1;
 *  grey, black, white and other hues are 0. */
export function magentaPurity(r: number, g: number, b: number): number {
  const top = Math.max(r, b);
  return top ? Math.max(0, Math.min(r, b) - g) / top : 0;
}

/** The guide region reaches this share of the short side beyond its strong magenta, so a pink
 *  reflection elsewhere on the phone or the backdrop is never keyed. */
const PURITY_REACH = 0.02;

/**
 * The purity key (owner, 2026-10-14: "key based on the … intensity"). Each pixel near the guide
 * is as transparent as it is magenta, on a smoothstep from `start` to `full`; there is no
 * yes/no step, so no patches. What stays keeps its light as black and white. `darkKeep` keeps
 * darker magenta more opaque.
 */
function purityKey(
  rgba: Uint8ClampedArray | Uint8Array, width: number, height: number,
  { start = 0.15, full = 0.6, darkKeep = 0, softness = 1, colour = 0 }: GuideKeyOptions,
): ChromaKeyResult | null {
  const n = width * height;
  const lo = Math.min(1, Math.max(0, start));
  const hi = Math.max(lo + 0.01, Math.min(1, full));
  const core = new Uint8Array(n);
  let cores = 0;
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    if (rgba[o + 3] >= 128 && magentaPurity(rgba[o], rgba[o + 1], rgba[o + 2]) >= hi) { core[i] = 1; cores++; }
  }
  if (!cores) return null;
  const region = grow(core, width, height, Math.max(1, Math.round(PURITY_REACH * Math.min(width, height))));
  const out = new Uint8ClampedArray(rgba);
  const keyed = new Uint8Array(n);
  const keep = Math.min(1, Math.max(0, darkKeep));
  for (let i = 0; i < n; i++) {
    const o = i * 4;
    if (!region[i] || !rgba[o + 3]) continue;
    const r = rgba[o], g = rgba[o + 1], b = rgba[o + 2];
    const t = Math.min(1, Math.max(0, (magentaPurity(r, g, b) - lo) / (hi - lo)));
    let k = t * t * (3 - 2 * t);
    if (!k) continue;
    const luma = 0.299 * r + 0.587 * g + 0.114 * b;
    k *= 1 - keep * (1 - luma / 255);
    keyed[i] = 1;
    out[o] = out[o + 1] = out[o + 2] = Math.round(luma);
    out[o + 3] = Math.round(rgba[o + 3] * (1 - k));
  }
  softenKeyEdge(out, rgba, width, height, softness);
  removeGuideCast(out, keyed, width, height, colour);
  const alpha = new Uint8Array(n);
  for (let i = 0; i < n; i++) alpha[i] = out[i * 4 + 3];
  return { out, alpha };
}

/**
 * The Transparent tab's key, as the bulk converter runs it (owner, 2026-10-14: "when we are
 * generating transparent image in bulk, it was generating correct"). Each pixel's magenta share
 * is unmixed from its colour, so the case's lines and shadows over the guide stay as
 * see-through colour. Colour removal then greys what is left beside the guide.
 */
function bulkKey(
  rgba: Uint8ClampedArray | Uint8Array, width: number, height: number,
  { noiseFloor = SHIPPED_KEYING.noiseFloor, snapLow = SHIPPED_KEYING.snapLow, snapHigh = SHIPPED_KEYING.snapHigh,
    flatSample = SHIPPED_KEYING.strongFrac, colour = 0 }: GuideKeyOptions,
): ChromaKeyResult | null {
  const res = keyImage(rgba, width, height, { noiseFloor, snapLow, snapHigh, strongFrac: flatSample });
  if (!res) return null;
  const n = width * height;
  const guide = new Uint8Array(n);
  for (let i = 0; i < n; i++) if (res.k[i] > 0.5) guide[i] = 1;
  removeGuideCast(res.out, guide, width, height, colour);
  const alpha = new Uint8Array(n);
  for (let i = 0; i < n; i++) alpha[i] = res.out[i * 4 + 3];
  return { out: res.out, alpha };
}

/** A tent of radius `softness` (0..4 px) over the keyed coverage, inside its own footprint only,
 *  so the step round what the key keeps is soft and an opening is never blurred into. At 1 it
 *  is the shipped 3×3 with weights 1-2-1. */
function softenKeyEdge(
  out: Uint8ClampedArray, rgba: Uint8ClampedArray | Uint8Array, width: number, height: number, softness: number,
): void {
  const n = width * height;
  // As the generated-guide engine detector does. The same alpha serves the displayed matte
  // and the saved print mask.
  const coverage = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const sourceAlpha = rgba[i * 4 + 3];
    coverage[i] = sourceAlpha ? Math.round(255 * (1 - out[i * 4 + 3] / sourceAlpha)) : 0;
  }
  const r = Math.round(Math.min(4, Math.max(0, softness)));
  for (let y = 0; y < height && r > 0; y++) for (let x = 0; x < width; x++) {
    const i = y * width + x;
    if (!coverage[i]) continue;
    let sum = 0, weight = 0;
    for (let py = Math.max(0, y - r); py <= Math.min(height - 1, y + r); py++) {
      for (let px = Math.max(0, x - r); px <= Math.min(width - 1, x + r); px++) {
        const tap = (r + 1 - Math.abs(px - x)) * (r + 1 - Math.abs(py - y));
        sum += coverage[py * width + px] * tap;
        weight += tap;
      }
    }
    const smooth = Math.min(coverage[i], Math.max(1, Math.round(sum / weight)));
    out[i * 4 + 3] = Math.round(rgba[i * 4 + 3] * (1 - smooth / 255));
  }
}

/** `mask` grown by `r` pixels: a running maximum along rows, then columns. */
function grow(mask: Uint8Array, w: number, h: number, r: number): Uint8Array {
  const rows = new Uint8Array(mask.length);
  for (let y = 0; y < h; y++) {
    let last = -Infinity;
    for (let x = 0; x < w; x++) { if (mask[y * w + x]) last = x; if (x - last <= r) rows[y * w + x] = 1; }
    last = Infinity;
    for (let x = w - 1; x >= 0; x--) { if (mask[y * w + x]) last = x; if (last - x <= r) rows[y * w + x] = 1; }
  }
  const out = new Uint8Array(mask.length);
  for (let x = 0; x < w; x++) {
    let last = -Infinity;
    for (let y = 0; y < h; y++) { if (rows[y * w + x]) last = y; if (y - last <= r) out[y * w + x] = 1; }
    last = Infinity;
    for (let y = h - 1; y >= 0; y--) { if (rows[y * w + x]) last = y; if (last - y <= r) out[y * w + x] = 1; }
  }
  return out;
}

export interface ChromaKeyResult {
  out: Uint8ClampedArray;
  alpha: Uint8Array;
}

/**
 * Key the magenta panel out of `rgba`. Returns null when the image carries no magenta panel
 * (the caller then keeps the image as it is and says so).
 */
export function chromaKeyFromPristine(
  rgba: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  shaded = false,
  options: GuideKeyOptions = {},
): ChromaKeyResult | null {
  if (shaded && options.method === "purity") return purityKey(rgba, width, height, options);
  if (shaded && options.method === "bulk") return bulkKey(rgba, width, height, options);
  const { tolerance = 0.5, softness = 1, colour = 0 } = options;
  const res = keyImage(rgba, width, height);
  if (!res) return null;
  const n = width * height;
  if (shaded) {
    // Opt-in only: grow from vivid marker cores through chromatic shadow/highlight
    // pixels. Never infer a printable surface from neutral white, nor key detached
    // weak pink reflections on the phone/background. Keep legacy export maths intact.
    // 0.5 → 42, the shipped threshold; 1 takes the faintest magenta, 0 only strong magenta.
    const eligibleScore = Math.round(84 * (1 - Math.min(1, Math.max(0, tolerance))));
    const takeFaint = Math.max(0, Math.min(1, tolerance) - 0.5) * 2;
    const eligible = new Uint8Array(n);
    const weak = new Uint8Array(n);
    const reached = new Uint8Array(n);
    const queue = new Int32Array(n);
    let head = 0, tail = 0;
    for (let i = 0; i < n; i++) {
      const o = i * 4;
      if (!rgba[o + 3]) continue;
      const r = rgba[o], g = rgba[o + 1], b = rgba[o + 2];
      const max = Math.max(r, b), min = Math.min(r, b), score = min - g;
      const shade = score >= 12 && (
        (score * 100 >= max * 65 && Math.abs(r - b) <= Math.max(12, Math.floor(max / 4))) ||
        (min >= 225 && Math.abs(r - b) <= 12)
      );
      if (score > eligibleScore || shade) eligible[i] = 1;
      if (shade) weak[i] = 1;
      if (score >= 100 && rgba[o + 3] >= 128) { reached[i] = 1; queue[tail++] = i; }
    }
    const visit = (i: number) => {
      if (eligible[i] && !reached[i]) { reached[i] = 1; queue[tail++] = i; }
    };
    while (head < tail) {
      const i = queue[head++], x = i % width;
      if (x > 0) visit(i - 1);
      if (x + 1 < width) visit(i + 1);
      if (i >= width) visit(i - width);
      if (i + width < n) visit(i + width);
    }
    for (let i = 0; i < n; i++) {
      const o = i * 4;
      if (!reached[i]) res.out.set(rgba.subarray(o, o + 4), o);
      else if (weak[i]) {
        // Luma is kept for the reference-light compositor instead of flattening
        // every removed panel pixel to white. Coverage is fully printable.
        const luma = Math.round(0.299 * rgba[o] + 0.587 * rgba[o + 1] + 0.114 * rgba[o + 2]);
        res.out[o] = luma; res.out[o + 1] = luma; res.out[o + 2] = luma; res.out[o + 3] = 0;
      } else {
        // Above 0.5 the tolerance also clears the faint magenta the fill reached: at 1, every
        // reached pixel that scores above the threshold is fully print guide.
        const score = Math.min(rgba[o], rgba[o + 2]) - rgba[o + 1];
        const clear = score > eligibleScore ? takeFaint : 0;
        res.out[o + 3] = Math.round(res.out[o + 3] * (1 - clear) * rgba[o + 3] / 255);
      }
    }
    softenKeyEdge(res.out, rgba, width, height, softness);
    removeGuideCast(res.out, reached, width, height, colour);
  }
  const alpha = new Uint8Array(n);
  for (let i = 0; i < n; i++) alpha[i] = res.out[i * 4 + 3];
  return { out: res.out, alpha };
}

/** The print-area coverage plane, expressed the way `MarkerDetection.print_mask` is: 255 =
 *  fully keyed panel, 0 = untouched product. Byte-for-byte `255 − ChromaKeyResult.alpha`. */
export interface ChromaMarker {
  printAlpha: Uint8Array;
  /** Keyed fraction 0..1 — same meaning and same "treat below 0.01 as no marker" rule as
   *  `MagentaMarkerResult.coverage`, so the two are interchangeable at the call site. */
  coverage: number;
}

/**
 * The same chroma key, expressed as the engine's print-area COVERAGE plane.
 *
 * WS-I / D-11: the editor bake used to build its print mask from the Rust
 * `detect_magenta_marker` (absolute `min(R,B)−G` ≥ 60 with a ±18 ramp) while the matte the
 * operator had just approved came from `keyImage` (relative, per-image flat estimate). Measured
 * on the 2048² reference those two disagree on 12.360 % of pixels, worst |Δα| = 166 — so the
 * operator approved one cutout and baked another (AUD-3 LB-8's failure scenario, verbatim).
 *
 * The print WINDOW they produce, after the bake's ×2 solidify, agrees to 0.0324 % of the frame,
 * which is why adopting THIS one costs ~1,359 px of a 4.2 Mpx render and buys exact
 * preview↔bake identity. See docs/superpowers/plans/2026-09-11-ws-i-keyer-backend-truth.md
 * ("The C-6 measurement") and the pinned oracle in
 * transparent/__fixtures__/keyer-parity.json.
 */
export function chromaMarkerFromPristine(
  rgba: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  shaded = false,
  options: GuideKeyOptions = {},
): ChromaMarker | null {
  const keyed = chromaKeyFromPristine(rgba, width, height, shaded, options);
  if (!keyed) return null;
  const printAlpha = new Uint8Array(width * height);
  for (let i = 0; i < printAlpha.length; i++) {
    const sourceAlpha = rgba[i * 4 + 3];
    printAlpha[i] = shaded
      ? sourceAlpha ? Math.round(255 * (1 - keyed.alpha[i] / sourceAlpha)) : 0
      : 255 - keyed.alpha[i];
  }
  return { printAlpha, coverage: shaded
    ? printAlpha.reduce((count, a) => count + Number(a > 0), 0) / Math.max(1, printAlpha.length)
    : inkFraction(keyed.alpha) };
}

/** The keyed fraction of an alpha plane: pixels whose alpha is below `below` (255 = any key
 *  at all; the owner's coverage check reads the mask's "takes ink" half, below 128). */
export function inkFraction(alpha: Uint8Array, below = 255): number {
  let keyed = 0;
  for (const a of alpha) if (a < below) keyed++;
  return keyed / Math.max(1, alpha.length);
}
